import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import Login from './components/Login';
import Sidebar, { BottomNav } from './components/Sidebar';
import FleetPage from './pages/FleetPage';
import TagoutPage from './pages/TagoutPage';
import SparePage from './pages/SparePage';
import ServiceLogPage from './pages/ServiceLogPage';
import { RequestsPage, InspectionsPage, UsersPage, ImportPage, ReportsPage, ModelsPage } from './pages/OtherPages';
import ToolDetailPage from './pages/ToolDetailPage';
import { supabase } from './lib/supabase';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function NotificationDropdown({ notifications, onMarkRead, onMarkAllRead }) {
  const fmt = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="notif-dropdown" style={{
      position: 'absolute', top: '100%', right: 0, width: '340px', maxHeight: '480px',
      background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 1000, overflow: 'hidden', marginTop: '8px',
    }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '14px' }}>Notifications</span>
        {notifications.some(n => !n.read) && (
          <button className="btn btn-ghost btn-sm" style={{ fontSize: '12px' }} onClick={onMarkAllRead}>Mark all read</button>
        )}
      </div>
      <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
        {notifications.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--gray-lt)', fontSize: '13px' }}>No notifications</div>
        ) : notifications.map(n => (
          <div key={n.id} onClick={() => onMarkRead(n.id)} style={{
            padding: '12px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer',
            background: n.read ? 'white' : '#F0FFF4', display: 'flex', gap: '10px', alignItems: 'flex-start',
          }}>
            {!n.read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#39D353', flexShrink: 0, marginTop: '5px' }} />}
            {n.read && <div style={{ width: '8px', flexShrink: 0 }} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: n.read ? 400 : 600, color: 'var(--black)', marginBottom: '2px' }}>{n.title}</div>
              {n.message && <div style={{ fontSize: '12px', color: 'var(--gray-mid)' }}>{n.message}</div>}
              <div style={{ fontSize: '11px', color: 'var(--gray-lt)', marginTop: '3px' }}>{fmt(n.created_at)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AppInner() {
  const { user, profile, loading, signOut, isAdmin, isManager, isExecutive, isTeamMember } = useAuth();
  const [active, setActive] = useState('fleet');
  const [pendingCount, setPendingCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const bellRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30);
    setNotifications(data || []);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    supabase.from('spare_requests').select('id', { count: 'exact' }).eq('status', 'pending').then(({ count }) => setPendingCount(count || 0));
  }, [user, active]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
  function handleClick(e) {
    if (bellRef.current && !bellRef.current.contains(e.target)) {
      setShowNotifications(false);
    }
    setShowUserMenu(false);
  }
  document.addEventListener('mousedown', handleClick);
  return () => document.removeEventListener('mousedown', handleClick);
}, []);

  async function markRead(id) {
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }

  async function markAllRead() {
    await supabase.from('notifications').update({ read: true }).eq('user_id', user.id).eq('read', false);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' }}>
      <div className="flex-center gap-1"><div className="spinner" /><span style={{ color: 'var(--gray-lt)' }}>Loading D&R Signs...</span></div>
    </div>
  );

  if (!user) return <Login />;

  const pages = {
    fleet: <FleetPage />,
    tagout: <TagoutPage />,
    spare: <SparePage />,
    browse: <SparePage isBrowse />,
    requests: <RequestsPage />,
    inspect: <InspectionsPage />,
    users: <UsersPage />,
    import: <ImportPage />,
    reports: <ReportsPage />,
    models: <ModelsPage />,
    servicelog: <ServiceLogPage />,
  };

  function getPage(id) {
    if (isExecutive && ['users', 'import', 'models', 'servicelog'].includes(id)) {
      return <ReportsPage />;
    }
    if (isTeamMember && ['spare', 'requests', 'servicelog', 'users', 'import', 'reports', 'models'].includes(id)) {
      return <FleetPage />;
    }
    return pages[id] || <FleetPage />;
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        {/* Hamburger — mobile only, opens drawer for overflow items */}
        <button className="hamburger" onClick={() => setShowSidebar(v => !v)} aria-label="Open menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>

        <a className="logo" href="#" onClick={e => { e.preventDefault(); setActive('fleet'); setShowSidebar(false); }}>
          <div className="logo-mark">
            <svg viewBox="0 0 22 22" fill="none">
              <rect x="1" y="3" width="14" height="10" rx="1.5" fill="#0F0F0F"/>
              <rect x="2" y="4" width="12" height="8" rx="1" fill="#39D353"/>
              <path d="M4 6h5M4 8h3M4 10h4" stroke="#0F0F0F" strokeWidth="1.2" strokeLinecap="round"/>
              <circle cx="17" cy="15" r="4" fill="#0F0F0F"/>
              <circle cx="17" cy="15" r="2.5" fill="#39D353"/>
            </svg>
          </div>
          <div>
            <div className="logo-text">D&R Signs</div>
            <span className="logo-sub">Tool Inventory</span>
          </div>
        </a>

        <div className="topbar-right">
          {/* Pending requests — hidden on mobile (badge shows in bottom nav instead) */}
          {pendingCount > 0 && (isAdmin || isManager) && (
            <button className="btn btn-warning btn-sm topbar-pending" onClick={() => setActive('requests')}>
              {pendingCount} pending request{pendingCount !== 1 ? 's' : ''}
            </button>
          )}

          {/* Bell */}
          <div ref={bellRef} style={{ position: 'relative' }}>
            <button
              className="btn btn-ghost btn-sm"
              style={{ position: 'relative', padding: '6px' }}
              onClick={() => { setShowNotifications(v => !v); loadNotifications(); }}
              aria-label="Notifications"
            >
              <BellIcon />
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: '2px', right: '2px',
                  background: '#C62828', color: 'white', borderRadius: '50%',
                  width: '16px', height: '16px', fontSize: '10px', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <NotificationDropdown
                notifications={notifications}
                onMarkRead={markRead}
                onMarkAllRead={markAllRead}
              />
            )}
          </div>

          {/* User chip — avatar always visible; name/role hidden on mobile via CSS */}
          <div style={{ position: 'relative' }}>
  <button
    className="user-chip"
    onClick={() => setShowUserMenu(v => !v)}
    style={{ border: 'none', cursor: 'pointer' }}
  >
    <div className="user-avatar">{getInitials(profile?.full_name)}</div>
    <div>
      <div className="user-name">{profile?.full_name || 'User'}</div>
      <div className="user-role">{profile?.role?.replace('_', ' ') || 'loading'}</div>
    </div>
  </button>
  {showUserMenu && (
    <div style={{
      position: 'absolute', top: 'calc(100% + 8px)', right: 0,
      background: 'white', border: '1px solid #E8E8E8',
      borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)',
      minWidth: '160px', zIndex: 300, overflow: 'hidden',
    }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #F0F0F0' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--black)' }}>{profile?.full_name}</div>
        <div style={{ fontSize: '11px', color: 'var(--green-dk)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{profile?.role?.replace('_', ' ')}</div>
      </div>
      <button
  className="btn btn-ghost btn-sm"
  style={{ width: '100%', borderRadius: 0, border: 'none', padding: '10px 14px', justifyContent: 'flex-start' }}
  onMouseDown={e => { e.stopPropagation(); signOut(); }}
>
  Sign out
</button>
    </div>
  )}
</div>

          {/* Sign out — hidden on mobile (accessible via sidebar drawer) */}
          <button className="btn btn-ghost btn-sm topbar-signout" onClick={signOut}>Sign out</button>
        </div>
      </header>

      <div className="main-layout">
        {/* Overlay behind open sidebar drawer */}
        <div className={`sidebar-overlay${showSidebar ? ' open' : ''}`} onClick={() => setShowSidebar(false)} />

        {/* Sidebar — desktop nav + mobile drawer */}
        <Sidebar
          active={active}
          setActive={setActive}
          pendingCount={pendingCount}
          isOpen={showSidebar}
          onClose={() => setShowSidebar(false)}
        />

        <main className="main-content">
          {getPage(active)}
        </main>
      </div>

      {/* Bottom nav — mobile only, rendered via CSS display:none on desktop */}
      <BottomNav
        active={active}
        setActive={setActive}
        pendingCount={pendingCount}
        onMoreClick={() => setShowSidebar(v => !v)}
      />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' }}>
      <div className="flex-center gap-1"><div className="spinner" /><span style={{ color: 'var(--gray-lt)' }}>Loading...</span></div>
    </div>
  );
  if (!user) return <Navigate to="/" state={{ from: location }} replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/tool/:id" element={
          <RequireAuth>
            <ToolDetailPage resourceType="tool" />
          </RequireAuth>
        } />
        <Route path="/consumable/:id" element={
          <RequireAuth>
            <ToolDetailPage resourceType="consumable" />
          </RequireAuth>
        } />
        <Route path="/*" element={<AppInner />} />
      </Routes>
    </AuthProvider>
  );
}